
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import {
    LayoutDashboard,
    FileText,
    Briefcase,
    LogOut,
    Menu,
    X
} from 'lucide-react';
import ContentEditor from '../../components/admin/ContentEditor';
import ProjectManager from '../../components/admin/ProjectManager';
import SkillsManager from '../../components/admin/SkillsManager';
import ExperienceManager from '../../components/admin/ExperienceManager';
import TestimonialsManager from '../../components/admin/TestimonialsManager';
import SocialLinksManager from '../../components/admin/SocialLinksManager';
import EducationManager from '../../components/admin/EducationManager';
import AchievementsManager from '../../components/admin/AchievementsManager';
import InterestsManager from '../../components/admin/InterestsManager';
import ResumeManager from '../../components/admin/ResumeManager';
import ShowcaseManager from '../../components/admin/ShowcaseManager';

export default function Dashboard() {
    const { signOut, user } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedSection, setSelectedSection] = useState<string | null>(null);
    const [stats, setStats] = useState({ projects: 0, sections: 0 });

    useEffect(() => {
        const fetchStats = async () => {
            const { count: projectCount } = await supabase.from('projects').select('*', { count: 'exact', head: true });
            const { count: sectionCount } = await supabase.from('site_content').select('*', { count: 'exact', head: true });
            setStats({
                projects: projectCount || 0,
                sections: sectionCount || 0
            });
        };

        if (user) fetchStats();
    }, [user]);

    const sections = [
        { id: 'hero', name: 'Hero Section' },
        { id: 'about', name: 'About Section' },
        { id: 'contact', name: 'Contact Info' },
        { id: 'skills', name: 'Skills' },
        { id: 'experience', name: 'Experience' },
        { id: 'testimonials', name: 'Testimonials' },
        { id: 'social_links', name: 'Social Links' },
        { id: 'about_education', name: 'About - Education' },
        { id: 'about_achievements', name: 'About - Achievements' },
        { id: 'about_resume', name: 'About - Resume' },
        { id: 'about_interests', name: 'About - Interests' },
        { id: 'projects_showcase', name: 'Projects Showcase' },
    ];

    const navigation = [
        { name: 'Overview', id: 'overview', icon: LayoutDashboard },
        { name: 'Site Content', id: 'content', icon: FileText },
        { name: 'Projects', id: 'projects', icon: Briefcase },
    ];

    return (
        <div className="min-h-screen bg-gray-950 text-white">
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/50 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <div className={`
        fixed inset-y-0 left-0 z-50 w-64 transform bg-gray-900 border-r border-gray-800 transition-transform duration-200 ease-in-out lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
                <div className="flex h-16 items-center justify-between px-6 border-b border-gray-800">
                    <span className="text-xl font-bold text-white">Admin Panel</span>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden text-gray-400 hover:text-white"
                    >
                        <X size={24} />
                    </button>
                </div>

                <nav className="mt-6 px-3 space-y-1">
                    {navigation.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => {
                                setActiveTab(item.id);
                                setSidebarOpen(false);
                            }}
                            className={`
                group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium transition-colors
                ${activeTab === item.id
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'}
              `}
                        >
                            <item.icon
                                className={`mr-3 h-5 w-5 flex-shrink-0 ${activeTab === item.id ? 'text-white' : 'text-gray-400 group-hover:text-white'
                                    }`}
                            />
                            {item.name}
                        </button>
                    ))}
                </nav>

                <div className="absolute bottom-0 w-full border-t border-gray-800 p-4">
                    <div className="flex items-center gap-3 mb-4 px-2">
                        <div className="h-8 w-8 rounded-full bg-blue-900/50 flex items-center justify-center border border-blue-800">
                            <span className="text-xs font-medium text-blue-200">
                                {user?.email?.substring(0, 2).toUpperCase()}
                            </span>
                        </div>
                        <div className="overflow-hidden">
                            <p className="truncate text-sm font-medium text-white">{user?.email}</p>
                        </div>
                    </div>
                    <button
                        onClick={() => signOut()}
                        className="flex w-full items-center rounded-md px-2 py-2 text-sm font-medium text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors"
                    >
                        <LogOut className="mr-3 h-5 w-5" />
                        Sign Out
                    </button>
                </div>
            </div>

            {/* Main content */}
            <div className="lg:pl-64">
                <div className="sticky top-0 z-10 flex h-16 items-center border-b border-gray-800 bg-gray-950/80 backdrop-blur-sm px-4 lg:px-8">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="mr-4 text-gray-400 hover:text-white lg:hidden"
                    >
                        <Menu size={24} />
                    </button>
                    <h1 className="text-xl font-semibold text-white capitalize">
                        {navigation.find(n => n.id === activeTab)?.name}
                    </h1>
                </div>

                <main className="p-4 lg:p-8">
                    {activeTab === 'overview' && (
                        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            <div className="rounded-xl border border-gray-800 bg-gray-900 p-6">
                                <h3 className="text-sm font-medium text-gray-400">Total Projects</h3>
                                <p className="mt-2 text-3xl font-bold text-white">{stats.projects}</p>
                            </div>
                            <div className="rounded-xl border border-gray-800 bg-gray-900 p-6">
                                <h3 className="text-sm font-medium text-gray-400">Content Sections</h3>
                                <p className="mt-2 text-3xl font-bold text-white">{stats.sections}</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'content' && (
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                            <div className="lg:col-span-1 space-y-2">
                                {sections.map((section) => (
                                    <button
                                        key={section.id}
                                        onClick={() => setSelectedSection(section.id)}
                                        className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${selectedSection === section.id
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-900 text-gray-400 hover:bg-gray-800 hover:text-white border border-gray-800'
                                            }`}
                                    >
                                        {section.name}
                                    </button>
                                ))}
                            </div>


                            {/* ... inside Dashboard rendering ... */}

                            <div className="lg:col-span-3">
                                {selectedSection === 'skills' ? (
                                    <SkillsManager />
                                ) : selectedSection === 'experience' ? (
                                    <ExperienceManager />
                                ) : selectedSection === 'testimonials' ? (
                                    <TestimonialsManager />
                                ) : selectedSection === 'social_links' ? (
                                    <SocialLinksManager />
                                ) : selectedSection === 'about_education' ? (
                                    <EducationManager />
                                ) : selectedSection === 'about_achievements' ? (
                                    <AchievementsManager />
                                ) : selectedSection === 'about_resume' ? (
                                    <ResumeManager />
                                ) : selectedSection === 'about_interests' ? (
                                    <InterestsManager />
                                ) : selectedSection === 'projects_showcase' ? (
                                    <ShowcaseManager />
                                ) : selectedSection ? (
                                    <ContentEditor
                                        key={selectedSection}
                                        sectionKey={selectedSection}
                                        title={sections.find(s => s.id === selectedSection)?.name || 'Editor'}
                                    />
                                ) : (
                                    <div className="rounded-xl border border-gray-800 bg-gray-900 p-12 text-center">
                                        <FileText className="mx-auto h-12 w-12 text-gray-600" />
                                        <h3 className="mt-2 text-sm font-semibold text-white">Select a Section</h3>
                                        <p className="mt-1 text-sm text-gray-400">Choose a section from the left to start editing.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'projects' && (
                        <ProjectManager />
                    )}
                </main>
            </div>
        </div>
    );
}
